import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import BankAccountSelector from './components/BankAccountSelector';
import StatementPeriodControl from './components/StatementPeriodControl';
import ImportToolbar from './components/ImportToolbar';
import BankTransactionPanel from './components/BankTransactionPanel';
import LedgerEntryPanel from './components/LedgerEntryPanel';
import MatchingArea from './components/MatchingArea';
import MatchedTransactionsSection from './components/MatchedTransactionsSection';
import DiscrepancySection from './components/DiscrepancySection';
import ReconciliationSummary from './components/ReconciliationSummary';
import MobileReconciliationTabs from './components/MobileReconciliationTabs';

const BankReconciliation = () => {
  const [selectedAccount, setSelectedAccount] = useState('acc-001');
  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-01-30');
  const [selectedBankTransactions, setSelectedBankTransactions] = useState([]);
  const [selectedLedgerEntries, setSelectedLedgerEntries] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedSource, setDraggedSource] = useState(null);

  const bankAccounts = [
    {
      id: 'acc-001',
      name: 'Business Checking',
      accountNumber: '****4521',
      balance: 125430.50
    },
    {
      id: 'acc-002',
      name: 'Savings Account',
      accountNumber: '****7892',
      balance: 50000.00
    },
    {
      id: 'acc-003',
      name: 'Payroll Account',
      accountNumber: '****3456',
      balance: 75200.25
    }
  ];

  const bankTransactions = [
    {
      id: 'bt-001',
      date: '01/15/2026',
      description: 'Payment from Acme Corp - Invoice #1234',
      reference: 'DEP-2026-001',
      amount: 5000.00,
      type: 'credit',
      status: 'unmatched',
      confidence: null
    },
    {
      id: 'bt-002',
      date: '01/16/2026',
      description: 'Office Supplies - Staples Inc',
      reference: 'CHK-2026-045',
      amount: -234.50,
      type: 'debit',
      status: 'unmatched',
      confidence: 95
    },
    {
      id: 'bt-003',
      date: '01/18/2026',
      description: 'Rent Payment - Property Management LLC',
      reference: 'ACH-2026-012',
      amount: -3500.00,
      type: 'debit',
      status: 'matched',
      confidence: null
    },
    {
      id: 'bt-004',
      date: '01/20/2026',
      description: 'Client Payment - Tech Solutions Inc',
      reference: 'DEP-2026-002',
      amount: 8750.00,
      type: 'credit',
      status: 'unmatched',
      confidence: 88
    },
    {
      id: 'bt-005',
      date: '01/22/2026',
      description: 'Bank Service Fee - Monthly Maintenance',
      reference: 'FEE-2026-001',
      amount: -25.00,
      type: 'debit',
      status: 'discrepancy',
      confidence: null
    },
    {
      id: 'bt-006',
      date: '01/25/2026',
      description: 'Utility Payment - Electric Company',
      reference: 'ACH-2026-015',
      amount: -450.75,
      type: 'debit',
      status: 'unmatched',
      confidence: 92
    },
    {
      id: 'bt-007',
      date: '01/28/2026',
      description: 'Interest Income - Monthly Interest',
      reference: 'INT-2026-001',
      amount: 125.50,
      type: 'credit',
      status: 'discrepancy',
      confidence: null
    }
  ];

  const ledgerEntries = [
    {
      id: 'le-001',
      date: '01/15/2026',
      description: 'Invoice Payment Received - Acme Corp',
      account: 'Accounts Receivable',
      reference: 'INV-1234',
      amount: 5000.00,
      type: 'debit'
    },
    {
      id: 'le-002',
      date: '01/16/2026',
      description: 'Office Supplies Purchase',
      account: 'Office Expenses',
      reference: 'EXP-045',
      amount: -234.50,
      type: 'credit'
    },
    {
      id: 'le-003',
      date: '01/18/2026',
      description: 'Monthly Rent Expense',
      account: 'Rent Expense',
      reference: 'RENT-JAN-2026',
      amount: -3500.00,
      type: 'credit'
    },
    {
      id: 'le-004',
      date: '01/20/2026',
      description: 'Service Revenue - Tech Solutions',
      account: 'Service Revenue',
      reference: 'INV-1245',
      amount: 8750.00,
      type: 'debit'
    },
    {
      id: 'le-005',
      date: '01/25/2026',
      description: 'Utility Expense - Electricity',
      account: 'Utilities Expense',
      reference: 'UTIL-JAN-2026',
      amount: -450.75,
      type: 'credit'
    }
  ];

  const suggestedMatches = [
    { bankId: 'bt-001', ledgerId: 'le-001', confidence: 98 },
    { bankId: 'bt-002', ledgerId: 'le-002', confidence: 95 },
    { bankId: 'bt-004', ledgerId: 'le-004', confidence: 88 },
    { bankId: 'bt-006', ledgerId: 'le-005', confidence: 92 }
  ];

  const [matches, setMatches] = useState([
    {
      id: 'match-001',
      description: 'Monthly Rent Payment',
      date: '01/18/2026',
      amount: 3500.00,
      matchType: 'One-to-One',
      bankTransactionCount: 1,
      ledgerEntryCount: 1
    }
  ]);

  const discrepancies = [
    {
      id: 'disc-001',
      type: 'bank_fee',
      description: 'Bank Service Fee - Monthly Maintenance',
      date: '01/22/2026',
      reference: 'FEE-2026-001',
      amount: -25.00,
      suggestedAction: 'Create a journal entry to record the bank fee in the general ledger under Bank Charges expense account.'
    },
    {
      id: 'disc-002',
      type: 'interest',
      description: 'Interest Income - Monthly Interest',
      date: '01/28/2026',
      reference: 'INT-2026-001',
      amount: 125.50,
      suggestedAction: 'Record the interest income in the general ledger under Interest Income revenue account.'
    }
  ];

  const reconciliationSummary = {
    accountName: 'Business Checking',
    period: 'January 2026',
    openingBalance: 115000.00,
    totalDeposits: 13875.50,
    totalWithdrawals: -4210.25,
    closingBalance: 124665.25,
    matchedCount: 1,
    unmatchedCount: 4,
    discrepancyCount: 2,
    totalCount: 7
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccount(accountId);
    setSelectedBankTransactions([]);
    setSelectedLedgerEntries([]);
  };

  const handleApplyPeriod = () => {
    console.log('Applying period:', startDate, 'to', endDate);
  };

  const handleImport = async (format) => {
    console.log('Importing', format, 'file');
    await new Promise(resolve => setTimeout(resolve, 1500));
  };

  const handleAutoMatch = async () => {
    console.log('Running auto-match algorithm');
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const newMatches = suggestedMatches?.filter(sm => sm?.confidence >= 90)?.map((sm, index) => {
        const bankTx = bankTransactions?.find(bt => bt?.id === sm?.bankId);
        const ledgerEntry = ledgerEntries?.find(le => le?.id === sm?.ledgerId);
        
        return {
          id: `match-${Date.now()}-${index}`,
          description: bankTx?.description,
          date: bankTx?.date,
          amount: Math.abs(bankTx?.amount),
          matchType: 'One-to-One',
          bankTransactionCount: 1,
          ledgerEntryCount: 1
        };
      });
    
    setMatches([...matches, ...newMatches]);
  };

  const handleClearMatches = () => {
    setSelectedBankTransactions([]);
    setSelectedLedgerEntries([]);
  };

  const handleBankTransactionSelect = (id) => {
    setSelectedBankTransactions(prev =>
      prev?.includes(id) ? prev?.filter(txId => txId !== id) : [...prev, id]
    );
  };

  const handleLedgerEntrySelect = (id) => {
    setSelectedLedgerEntries(prev =>
      prev?.includes(id) ? prev?.filter(entryId => entryId !== id) : [...prev, id]
    );
  };

  const handleDragStart = (e, item, source) => {
    setDraggedItem(item);
    setDraggedSource(source);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    if (draggedItem && draggedSource) {
      if (draggedSource === 'bank') {
        handleBankTransactionSelect(draggedItem?.id);
      } else {
        handleLedgerEntrySelect(draggedItem?.id);
      }
    }
    setDraggedItem(null);
    setDraggedSource(null);
  };

  const handleMatch = () => {
    if (selectedBankTransactions?.length > 0 && selectedLedgerEntries?.length > 0) {
      const bankTx = bankTransactions?.find(bt => bt?.id === selectedBankTransactions?.[0]);
      
      const newMatch = {
        id: `match-${Date.now()}`,
        description: bankTx?.description,
        date: bankTx?.date,
        amount: Math.abs(bankTx?.amount),
        matchType: selectedBankTransactions?.length === 1 && selectedLedgerEntries?.length === 1 
          ? 'One-to-One' 
          : selectedBankTransactions?.length > 1 
          ? 'Many-to-One' :'One-to-Many',
        bankTransactionCount: selectedBankTransactions?.length,
        ledgerEntryCount: selectedLedgerEntries?.length
      };
      
      setMatches([...matches, newMatch]);
      setSelectedBankTransactions([]);
      setSelectedLedgerEntries([]);
    }
  };

  const handleUnmatch = () => {
    setSelectedBankTransactions([]);
    setSelectedLedgerEntries([]);
  };

  const handleUnmatchTransaction = (matchId) => {
    setMatches(matches?.filter(m => m?.id !== matchId));
  };

  const handleViewMatchDetails = (matchId) => {
    console.log('Viewing details for match:', matchId);
  };

  const handleResolveDiscrepancy = (discrepancyId, action) => {
    console.log('Resolving discrepancy:', discrepancyId, 'with action:', action);
  };

  const handleAddAdjustment = () => {
    console.log('Adding manual adjustment');
  };

  const handleCompleteReconciliation = () => {
    console.log('Completing reconciliation');
  };

  const handleExportReport = () => {
    console.log('Exporting reconciliation report');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      <main className="pt-20 pb-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-[1920px] mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-2">
              Bank Reconciliation
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground">
              Match bank transactions with accounting records to ensure accurate financial statements
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
            <BankAccountSelector
              selectedAccount={selectedAccount}
              onAccountChange={handleAccountChange}
              accounts={bankAccounts}
            />
            <StatementPeriodControl
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onApply={handleApplyPeriod}
            />
            <ImportToolbar
              onImport={handleImport}
              onAutoMatch={handleAutoMatch}
              onClearMatches={handleClearMatches}
              matchedCount={matches?.length}
              totalCount={bankTransactions?.length}
            />
          </div>

          <MobileReconciliationTabs
            bankTransactions={bankTransactions}
            ledgerEntries={ledgerEntries}
            selectedBankTransactions={selectedBankTransactions}
            selectedLedgerEntries={selectedLedgerEntries}
            onBankTransactionSelect={handleBankTransactionSelect}
            onLedgerEntrySelect={handleLedgerEntrySelect}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
            onMatch={handleMatch}
            onUnmatch={handleUnmatch}
            suggestedMatches={suggestedMatches}
            draggedItem={draggedItem}
            draggedSource={draggedSource}
          />

          <div className="hidden lg:grid lg:grid-cols-12 gap-6 mb-6">
            <div className="lg:col-span-4 h-[600px]">
              <BankTransactionPanel
                transactions={bankTransactions}
                onTransactionSelect={handleBankTransactionSelect}
                selectedTransactions={selectedBankTransactions}
                onDragStart={handleDragStart}
              />
            </div>

            <div className="lg:col-span-4 h-[600px]">
              <MatchingArea
                draggedItem={draggedItem}
                draggedSource={draggedSource}
                onDrop={handleDrop}
                onMatch={handleMatch}
                onUnmatch={handleUnmatch}
                selectedBankTransactions={selectedBankTransactions}
                selectedLedgerEntries={selectedLedgerEntries}
                bankTransactions={bankTransactions}
                ledgerEntries={ledgerEntries}
              />
            </div>

            <div className="lg:col-span-4 h-[600px]">
              <LedgerEntryPanel
                entries={ledgerEntries}
                onEntrySelect={handleLedgerEntrySelect}
                selectedEntries={selectedLedgerEntries}
                onDragStart={handleDragStart}
                suggestedMatches={suggestedMatches}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
            <MatchedTransactionsSection
              matches={matches}
              onUnmatch={handleUnmatchTransaction}
              onViewDetails={handleViewMatchDetails}
            />
            <DiscrepancySection
              discrepancies={discrepancies}
              onResolve={handleResolveDiscrepancy}
              onAddAdjustment={handleAddAdjustment}
            />
          </div>

          <ReconciliationSummary
            summary={reconciliationSummary}
            onComplete={handleCompleteReconciliation}
            onExport={handleExportReport}
          />
        </div>
      </main>
    </div>
  );
};

export default BankReconciliation;